/**
 * The five report stages and the transitions the system is allowed to make between them.
 *
 * Authority: `05-reporting-marking.md` §A3 (the three progress stat boxes, which count
 * `stage ≥ 2`, `≥ 3` and `≥ 4`), §A4 and `04-officials-profiles.md` §A3 (the five stages
 * and their tooltips, verbatim), §B6 (the `Push To TMS` / `Repush To TMS` relabel and the
 * push guards) and §B9 (the state table, including the rule that `TMS Published` is **read
 * back from TMS, never set by us**).
 *
 * It lives in `packages/shared` for the same reason the criteria and the outcome formula
 * do: the API decides which transitions it will accept, and the Report Studio decides which
 * buttons it will draw, and those two answers must be the same answer. A screen that offers
 * a transition the route refuses is a lie told in a button.
 *
 * **Only the furthest stage reached is stored** (`10-data-model.md` §6, migration 006), so
 * the map of deliberate transitions below never steps backwards. The one place a stage does
 * move back is {@link stageAfterTextSave}, and it is not a transition anybody asks for: an
 * unpushed report whose narrative is cleared returns to `Blank`, because ruling D-4 forbids
 * the textless `TD Drafted` it would otherwise sit on. Nothing at `TMS Draft` or above ever
 * steps back — TMS holds that push whatever we do to our copy.
 *
 * The one self-transition — `3 → 3` — is the repush of §B6:
 * the text is written to TMS again and the stage stays where it is, which is precisely why
 * the button relabels rather than advancing anything.
 *
 * British English throughout, per the standing copy rule.
 */
/** The five stages, in pipeline order — `Blank → AI Drafted → TD Drafted → TMS Draft → TMS Published` (§A4). */
export const REPORT_STAGES = [0, 1, 2, 3, 4];
/** The pill labels, verbatim from `04-officials-profiles.md` §A3. */
export const REPORT_STAGE_LABELS = {
    0: "Blank",
    1: "AI Drafted",
    2: "TD Drafted",
    3: "TMS Draft",
    4: "TMS Published",
};
/** The pill tooltips, verbatim from `04-officials-profiles.md` §A3. */
export const REPORT_STAGE_TOOLTIPS = {
    0: "Nothing Written Yet",
    1: "AI Draft Generated — Not Yet Copied Into The TD Draft",
    2: "TD Draft Report Written — Not Yet Pushed",
    3: "Pushed To TMS As Draft",
    4: "Confirmed And Published In TMS",
};
/**
 * `TMS Published` — the released stage.
 *
 * §B9: *"Confirmed in TMS. Read back from TMS, never set by us."* A report that has reached
 * it has left our custody: the text in TMS is the published record, and editing our copy
 * afterwards would leave the Control Centre asserting something TMS does not hold. Every
 * write path therefore refuses a released report outright.
 */
export const REPORT_STAGE_RELEASED = 4;
/** True once the report is published in TMS and immutable to us (§B9). */
export function isReportReleased(stage) {
    return stage === REPORT_STAGE_RELEASED;
}
/**
 * The transitions **this system** may make, by the stage the report is on now.
 *
 *  * `0 → 1` an AI draft is generated (§B9 `AI Drafted`).
 *  * `0 → 2`, `1 → 2` the TD's own draft holds text (§B9 `TD Drafted`). `0 → 2` is legal
 *    because the AI draft is optional — §B6's textarea is empty by default and the TD may
 *    simply write his own.
 *  * `2 → 3` the push, write path W2 for the TD's subjects and W3 for an umpire (§B6).
 *  * `3 → 3` the **repush** of §B6 — the button relabels `Repush To TMS`, the text is sent
 *    again and the stage does not move, because there is nowhere further for it to go.
 *  * `4 → ∅` nothing. A released report is immutable to us.
 *
 * **Nothing here transitions to 4.** `TMS Published` is set by reading TMS back
 * (`08-tms-read-layer.md`), never by an action in this application, so it is deliberately
 * absent from every list rather than present and guarded elsewhere.
 */
export const ALLOWED_REPORT_STAGE_TRANSITIONS = {
    0: [1, 2],
    1: [2],
    2: [3],
    3: [3],
    4: [],
};
/** The stages a report on `from` may legally move to. Empty once released. */
export function legalReportStageTransitions(from) {
    return ALLOWED_REPORT_STAGE_TRANSITIONS[from];
}
/** True when `from → to` is one of the transitions above. */
export function isLegalReportStageTransition(from, to) {
    return legalReportStageTransitions(from).includes(to);
}
/**
 * The two **marker** transitions — the stages that assert a draft exists rather than send it
 * anywhere (`AI Drafted`, `TD Drafted`).
 *
 * Ruling D-4 (`rulings-2026-08-11-open-questions.md` §G, 12 August 2026): *"A report may not
 * be marked `AI Drafted` (stage 1) or `TD Drafted` (stage 2) while it holds no narrative
 * text."* §A3's stat boxes count officials by stage on the premise that the stage means the
 * draft holds text, and the progress counters must never overstate: a textless stage-2 report
 * would inflate `Reports Drafted`.
 *
 * `TMS Draft` (3) is **not** here. It has §B6's own `Nothing To Push` guard, which refuses an
 * empty push in its own words and measures the assembled text that would actually be sent;
 * and `TMS Published` (4) is read back from TMS and never set here at all.
 */
export function stageRequiresReportText(to) {
    return to === 1 || to === 2;
}
/**
 * What the Report Studio says beside the withheld markers (ruling D-4).
 *
 * It lives here so the screen's explanation and the route's rule are changed together, and
 * it names the remedy rather than the refusal — the TD's next act is to type, not to read.
 * British English, per the standing copy rule.
 */
export const REPORT_TEXT_REQUIRED_HINT = "Add text before marking this drafted.";
/**
 * The button copy for one transition.
 *
 * `Push To TMS` and `Repush To TMS` are verbatim from §B6 — the control relabels after the
 * first successful push. The other two say plainly what they record, because neither is a
 * push and neither sends anything anywhere.
 */
export function reportStageActionLabel(from, to) {
    if (to === 3)
        return from === 3 ? "Repush To TMS" : "Push To TMS";
    if (to === 1)
        return "Mark AI Drafted";
    return "Mark TD Drafted";
}
/**
 * The stage a report sits on after a text write — given the stage it is on now and whether
 * the saved report still holds text.
 *
 * §A3's first stat box counts *"officials whose **TD Draft Report** holds text — whether or
 * not it has been pushed"*, which is `stage ≥ 2`. Saving text therefore carries a blank or
 * AI-drafted report forward to `TD Drafted` on its own, with no second button press.
 *
 * **And back.** Ruling D-4 (§G) says a report *"may not be marked `AI Drafted` (stage 1) or
 * `TD Drafted` (stage 2) while it holds no narrative text"*, and that is a property of the
 * report rather than of the moment it was written: a rule enforced only on the way up leaves
 * the very state it forbids reachable by writing text and then clearing it, with §A3's
 * counters overstating `Reports Drafted` for a report holding nothing. So an **unpushed**
 * report — stage 1 or 2 — whose text is cleared returns to `Blank`.
 *
 * **From stage 3 upwards it still never steps back.** `TMS Draft` and `TMS Published` are not
 * claims about our copy of the text: TMS really does hold that push, and clearing our
 * narrative afterwards does not unsend it. Stepping those back would be the system asserting
 * something about TMS that is untrue — the mirror image of the defect above. (Stage 4 refuses
 * every write long before this is reached; it is covered here so the function is total.)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO FACTS, BECAUSE THE TWO DIRECTIONS ANSWER TWO QUESTIONS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `holdsNarrative` — the **TD Draft Report** narrative is not empty. Going **up** is the
 * system making a claim on the TD's behalf without being asked, so it makes it only from the
 * thing §A3's stat box actually names. On an umpire's report the narrative is the owning UM's
 * (§A5, W3), so the TD's closing remarks alone do not announce that her report is drafted —
 * that would overstate `Reports Drafted` for a report she has not written.
 *
 * `holdsText` — the **assembled** text is not empty, which is `reportHoldsText`'s definition
 * in the API's reports repository and ruling D-4's. Coming **down** is the system withdrawing
 * a claim that has become untrue, and D-4's claim is untrue only when the report holds
 * nothing at all. So an umpire's report keeps its stage while the TD's closing remarks stand,
 * even once her narrative is cleared — exactly the state D-4 permits to be marked drafted by
 * hand.
 *
 * The two coincide on every report the TD owns, where the assembled text **is** the
 * narrative — which is why `holdsText` defaults to `holdsNarrative`, and why a caller with no
 * assembly to worry about may pass one boolean and be right.
 */
export function stageAfterTextSave(current, holdsNarrative, holdsText = holdsNarrative) {
    if (current >= 3)
        return current;
    if (!holdsText)
        return 0;
    return holdsNarrative && current < 2 ? 2 : current;
}
//# sourceMappingURL=report-stages.js.map